import L from 'leaflet';
import { useMap } from 'react-leaflet';
import { useEffect, useRef } from 'react';

// ---- Color-coded marker icons by risk level ----

const MARKER_COLORS = {
  high: 'red',
  medium: 'orange',
  low: 'green',
  unknown: 'blue',
};

const markerIconCache = {};

function classifyRisk(value) {
  if (!value) return null;
  const v = String(value).toLowerCase().trim();

  // ── HIGH: Tier 3, Level 3, Predator, SVP, Lifetime, Murder ──
  if (v === '3' || v === 'high') return 'high';
  if (v.includes('tier 3') || v.includes('tier iii') || v.includes('level 3')) return 'high';
  if (v.includes('predator') || v.includes('svp')) return 'high';
  if (v.includes('lifetime') || v.includes('murder')) return 'high';
  if (v.includes('high risk')) return 'high';
  if (v.includes('civil commitment')) return 'high';
  if (v.includes('violent') && !v.includes('non')) return 'high';
  if (v.includes('aggravated')) return 'high';

  // ── MEDIUM: Tier 2, Moderate, Repetitive, Out-of-State ──
  if (v === '2' || v.includes('moderate')) return 'medium';
  if (v.includes('tier 2') || v.includes('tier ii')) return 'medium';
  if (v.includes('repetitive') || v.includes('compulsive')) return 'medium';
  if (v.includes('out-of-state') || v.includes('out of state')) return 'medium';
  if (v.includes('ten-year') || v.includes('ten year')) return 'medium';

  // ── LOW: Tier 1, Low, Offender (generic), Sex Offender (generic) ──
  if (v === '1' || v.includes('low')) return 'low';
  if (v.includes('tier 1') || v.includes('tier i')) return 'low';
  if (v === 'offender' || v === 'sex offender') return 'low';
  if (v.includes('juvenile') || v.includes('pre-sorna')) return 'low';
  if (v.includes('sexually oriented offender')) return 'low';
  if (v.includes('offender against children')) return 'medium';

  // ── Skip non-informative values ──
  if (v === 'not reported' || v === 'unknown' || v === 'pending' || v.includes('awaiting')) return null;

  return null;
}

export function getRiskColor(riskLevel, tierLevel) {
  const cls = classifyRisk(riskLevel) || classifyRisk(tierLevel);
  return MARKER_COLORS[cls || 'unknown'];
}

export function getRiskLabel(riskLevel, tierLevel) {
  const cls = classifyRisk(riskLevel) || classifyRisk(tierLevel);
  if (cls === 'high') return 'High';
  if (cls === 'medium') return 'Medium';
  if (cls === 'low') return 'Low';
  return 'Unknown';
}

export function getRiskBadgeClass(riskLabel) {
  switch (riskLabel) {
    case 'High': return 'pr-risk-badge pr-risk-high';
    case 'Medium': return 'pr-risk-badge pr-risk-medium';
    case 'Low': return 'pr-risk-badge pr-risk-low';
    default: return 'pr-risk-badge pr-risk-unknown';
  }
}

export function getMarkerIcon(riskLevel, tierLevel) {
  const color = getRiskColor(riskLevel, tierLevel);
  if (markerIconCache[color]) return markerIconCache[color];

  const icon = L.icon({
    iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

  markerIconCache[color] = icon;
  return icon;
}

// ---- User location marker (blue pulsing dot) ----

export const userLocationIcon = L.divIcon({
  className: 'pr-user-location-marker',
  html: '<div class="pr-user-dot"><div class="pr-user-dot-pulse"></div></div>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  popupAnchor: [0, -10],
});

// ---- ChangeView utility component ----

export function ChangeView({ center, zoom }) {
  const map = useMap();
  const prevCenterRef = useRef(null);
  const prevZoomRef = useRef(null);

  useEffect(() => {
    if (!center || !center[0] || !center[1]) return;

    const prev = prevCenterRef.current;
    const centerChanged = !prev ||
      Math.abs(prev[0] - center[0]) > 0.0005 ||
      Math.abs(prev[1] - center[1]) > 0.0005;
    const zoomChanged = prevZoomRef.current !== zoom;

    if (centerChanged || zoomChanged) {
      // Big move (>1 degree, e.g. default center → user location): use provided zoom
      // Small move: keep user's current zoom
      const isBigMove = !prev ||
        Math.abs(prev[0] - center[0]) > 1 ||
        Math.abs(prev[1] - center[1]) > 1;

      const targetZoom = isBigMove ? zoom : map.getZoom();

      prevCenterRef.current = [center[0], center[1]];
      prevZoomRef.current = zoom;

      if (isBigMove) {
        map.flyTo(center, targetZoom, { duration: 1.2 });
      } else if (centerChanged) {
        map.setView(center, targetZoom);
      }

      const timer = setTimeout(() => map.invalidateSize(), 100);
      return () => clearTimeout(timer);
    }
  }, [center, zoom, map]);

  return null;
}

// ---- Heatmap layer component ----

export function HeatmapLayer({ points, options = {} }) {
  const map = useMap();
  const heatLayerRef = useRef(null);

  useEffect(() => {
    // Dynamically import leaflet.heat
    import('leaflet.heat').then(() => {
      if (heatLayerRef.current) {
        map.removeLayer(heatLayerRef.current);
      }

      if (points.length === 0) return;

      const heatPoints = points.map(p => [p[0], p[1], p[2] || 1.0]);
      heatLayerRef.current = L.heatLayer(heatPoints, {
        radius: 25,
        blur: 15,
        maxZoom: 17,
        max: 1.0,
        gradient: { 0.2: 'blue', 0.4: 'lime', 0.6: 'yellow', 0.8: 'orange', 1.0: 'red' },
        ...options,
      }).addTo(map);
    });

    return () => {
      if (heatLayerRef.current) {
        map.removeLayer(heatLayerRef.current);
        heatLayerRef.current = null;
      }
    };
  }, [map, points, options]);

  return null;
}

// ---- Distance calculation (Haversine) for client-side use ----

export function calculateDistanceMiles(lat1, lon1, lat2, lon2) {
  const R = 3959; // Radius of earth in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// ---- Format popup address ----

export function formatPopupAddress(offender) {
  const parts = [offender.address_line1, offender.city, offender.state_id, offender.zip_code].filter(Boolean);
  return parts.join(', ') || 'Address not available';
}
